import { Component, OnInit, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

// Validator
import { PasswordValidation } from '../../validators/password/password.validator';

@Component({
  selector: 'custom-input',
  templateUrl: './custom-input.component.html',
  styleUrls: ['./custom-input.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CustomInputComponent),
    multi: true
  }]
})
export class CustomInputComponent implements OnInit, ControlValueAccessor {

  // Inputs
  @Input() error: any = false;
  @Input() options: any = '';
  @Input() optional: boolean = false;
  @Input() highlight: boolean = false;
  @Input() highlightMessage: string = '';

  // Public variables
  public opts: any = '';
  public invalidField: boolean = false;
  public showStrongness: boolean = false;
  public strongPassword: boolean = false;
  public showingPassword: boolean = false;
  public showRequirements: boolean = false;
  public showDisplayPassword: boolean = false;

  // The internal data model
  private innerValue: any = '';
  private errorMessage: string = '';
  private passwordValidations: any;

  // Placeholders for the callbacks which are later provided by the Control Value Accessor
  private onChangeCallback: (_: any) => {};

  // Get accessor
  get value(): any {
    return this.innerValue;
  }

  // Set accessor including call the onchange callback
  set value(v: any) {
    v = v.replace(/\s/g, '');
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.onChangeCallback(v);
    }
  }

  // Constructor
  constructor() { }

  // On component init
  ngOnInit() {
    // If the input type is password show the display button
    this.showDisplayPassword = this.options.inputType === 'password';
    if (this.options.value) this.invalidField = this.error;

    // Save error message
    this.errorMessage = this.options.errorMessage;

    // Save options
    this.opts = Object.assign({}, this.options);
  }

  // From ControlValueAccessor interface
  public writeValue(value: any) {
    // Reset input status
    if (!value) {
      this.highlight = false;
      this.invalidField = false;
      this.showStrongness = false;
    }

    // Set new inner value
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
  }

  // From ControlValueAccessor interface
  public registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  public registerOnTouched(fn: any) { }

  // Display password
  public displayPassword(): void {
    this.opts.inputType = (this.opts.inputType === 'password') ? 'text' : 'password';
    this.showingPassword = !this.showingPassword;
  }

  // On input blur
  public onInputBlur(): void {
    this.invalidField = this.error;
    if (this.showRequirements) this.showRequirements = false;

    // Change new error message
    if (this.opts.errorMessages && this.error) {
      let errorKey = this.error.key || Object.keys(this.error)[0];
      errorKey = errorKey.charAt(0).toUpperCase() + errorKey.slice(1);

      this.opts.errorMessage = this.opts.errorMessages[errorKey];
    } else if (!this.innerValue) {
      this.opts.errorMessage = (this.opts.requiredMessage) ? this.opts.requiredMessage : this.opts.errorMessage;
    } else {
      this.opts.errorMessage = this.errorMessage;
    }
  }

  // On input focus
  public onInputFocus(): void {
    if (this.opts.inputRequirements) this.showRequirements = true;
  }

  // On keyboard key down
  public onKeyDown(event): boolean {
    if (this.opts.inputType === 'password') return event.which !== 32;
  }

  // On keyboard key up
  public onKeyUp() {
    this.showStrongness = this.opts.inputRequirements.weak && (this.innerValue && this.innerValue.length > 0);
    this.validateRecomendations(this.innerValue);
  }

  // On paste
  public onInputPaste(event: any) {
    if (this.opts.inputIcon.indexOf('repeat-password') !== -1) event.preventDefault();
  }

  // Password recomendations
  public validateRecomendations(password: string): void {
    this.passwordValidations = PasswordValidation.Password(password);

    // Assign the proper value for each item
    if (this.opts.inputRequirements.items) {
      for (let i = 0; i < this.opts.inputRequirements.items.length; i++) {
        this.opts.inputRequirements.items[i].done = (this.passwordValidations !== true) ? this.passwordValidations[this.opts.inputRequirements.items[i].key] : true;
      }
    }

    // Strong password
    this.strongPassword = this.passwordValidations === true;
  }

  // Evaluate other forms error
  public evaluateErrors() {
    if (this.error && this.opts.errorMessages) {
      const ERROR: string = this.opts.errorMessages[this.error.key];
      if (ERROR && ERROR.indexOf('REQUIRED') === -1 && ERROR.indexOf('NOT_MATCH') === -1) return ERROR;
    }

    if (!this.error && this.invalidField) setTimeout(() => this.invalidField = false);
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/components/custom-input/custom-input.component.ts